import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Plus, UserPlus, DollarSign, Trash2, Edit2, Save, Percent, Calendar } from 'lucide-react';
import { useHouseholdStore, HouseholdMember } from '../store/householdStore';
import { Input } from './Input';
import toast from 'react-hot-toast';

interface HouseholdMembersProps {
  className?: string;
}

export const HouseholdMembers: React.FC<HouseholdMembersProps> = ({ className = '' }) => {
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [editingMember, setEditingMember] = useState<string | null>(null);
  const [newMember, setNewMember] = useState<Omit<HouseholdMember, 'id'>>({
    name: '',
    monthlyIncome: 0,
    expenses: [],
    savingsGoals: [],
    debts: []
  });

  const {
    members,
    addMember,
    updateMember,
    removeMember,
    addExpense,
    updateExpense,
    removeExpense
  } = useHouseholdStore();

  const handleAddMember = () => {
    if (!newMember.name) {
      toast.error('Please enter a name for the household member');
      return;
    }

    addMember(newMember);
    setIsAddingMember(false);
    setNewMember({
      name: '',
      monthlyIncome: 0,
      expenses: [],
      savingsGoals: [],
      debts: []
    });
    toast.success('Household member added successfully');
  };

  const handleAddExpense = (memberId: string) => {
    addExpense(memberId, {
      name: '',
      amount: 0,
      category: 'needs',
      isShared: false
    });
  };

  const calculateTotalIncome = () => {
    return members.reduce((total, member) => total + member.monthlyIncome, 0);
  };

  const calculateSharedExpenses = () => {
    return members.reduce((total, member) => {
      return total + member.expenses
        .filter(expense => expense.isShared)
        .reduce((sum, expense) => sum + expense.amount, 0);
    }, 0);
  };

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-emerald-500/10">
            <Users className="w-6 h-6 text-emerald-500" />
          </div>
          <h2 className="text-2xl font-semibold">Household Members</h2>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setIsAddingMember(true)}
          className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors flex items-center gap-2"
        >
          <UserPlus className="w-4 h-4" />
          Add Member
        </motion.button>
      </div>

      <AnimatePresence>
        {isAddingMember && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="glass-card p-6 border border-emerald-500/20"
          >
            <h3 className="text-lg font-semibold mb-4">Add Household Member</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Input
                label="Name"
                value={newMember.name}
                onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                placeholder="Enter name"
                type="text"
              />
              <Input
                label="Monthly Income"
                icon={DollarSign}
                value={newMember.monthlyIncome}
                onValueChange={(value) => setNewMember({ ...newMember, monthlyIncome: Number(value.replace(/,/g, '')) })}
                type="number"
                min="0"
              />
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setIsAddingMember(false)}
                className="px-4 py-2 bg-slate-500/10 text-slate-300 rounded-lg hover:bg-slate-500/20 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddMember}
                className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save Member
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="glass-card p-6 border border-emerald-500/20">
          <h3 className="text-lg font-semibold text-emerald-500 mb-2">Total Household Income</h3>
          <p className="text-3xl font-bold">${calculateTotalIncome().toLocaleString()}</p>
        </div>
        <div className="glass-card p-6 border border-emerald-500/20">
          <h3 className="text-lg font-semibold text-emerald-500 mb-2">Shared Expenses</h3>
          <p className="text-3xl font-bold">${calculateSharedExpenses().toLocaleString()}</p>
        </div>
        <div className="glass-card p-6 border border-emerald-500/20">
          <h3 className="text-lg font-semibold text-emerald-500 mb-2">Members</h3>
          <p className="text-3xl font-bold">{members.length}</p>
        </div>
      </div>

      <div className="space-y-6">
        {members.map(member => (
          <motion.div
            key={member.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-6 border border-emerald-500/20"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-semibold">{member.name}</h3>
                <p className="text-slate-400">Monthly Income: ${member.monthlyIncome.toLocaleString()}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setEditingMember(member.id === editingMember ? null : member.id)}
                  className="p-2 hover:bg-emerald-500/10 rounded-lg transition-colors"
                >
                  <Edit2 className="w-4 h-4 text-emerald-500" />
                </button>
                <button
                  onClick={() => removeMember(member.id)}
                  className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
            </div>

            <AnimatePresence>
              {editingMember === member.id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-6"
                >
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      label="Name"
                      value={member.name}
                      onChange={(e) => updateMember(member.id, { name: e.target.value })}
                      type="text"
                    />
                    <Input
                      label="Monthly Income"
                      icon={DollarSign}
                      value={member.monthlyIncome}
                      onValueChange={(value) => updateMember(member.id, { monthlyIncome: Number(value.replace(/,/g, '')) })}
                      type="number"
                      min="0"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Expenses</h4>
                <button
                  onClick={() => handleAddExpense(member.id)}
                  className="px-3 py-1 text-sm bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add Expense
                </button>
              </div>

              {member.expenses.map(expense => (
                <div
                  key={expense.id}
                  className="glass-card p-4 hover:bg-slate-800/30 transition-colors"
                >
                  <div className="grid md:grid-cols-4 gap-4">
                    <Input
                      value={expense.name}
                      onChange={(e) => updateExpense(member.id, expense.id, { name: e.target.value })}
                      placeholder="Expense name"
                    />
                    <Input
                      icon={DollarSign}
                      value={expense.amount}
                      onValueChange={(value) => updateExpense(member.id, expense.id, { amount: Number(value.replace(/,/g, '')) })}
                      type="number"
                      min="0"
                    />
                    <select
                      value={expense.category}
                      onChange={(e) => updateExpense(member.id, expense.id, { category: e.target.value as 'needs' | 'wants' | 'savings' })}
                      className="glass-input px-4 py-2 rounded-lg"
                    >
                      <option value="needs">Needs</option>
                      <option value="wants">Wants</option>
                      <option value="savings">Savings</option>
                    </select>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={expense.isShared}
                          onChange={(e) => updateExpense(member.id, expense.id, { isShared: e.target.checked })}
                          className="rounded text-emerald-500 focus:ring-emerald-500"
                        />
                        <span className="text-sm text-slate-400">Shared</span>
                      </label>
                      {expense.isShared && (
                        <Input
                          icon={Percent}
                          value={expense.contributionRatio || 50}
                          onValueChange={(value) => updateExpense(member.id, expense.id, { contributionRatio: Number(value) })}
                          type="number"
                          min="0"
                          max="100"
                          className="w-24"
                        />
                      )}
                      <button
                        onClick={() => removeExpense(member.id, expense.id)}
                        className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors ml-auto"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {member.expenses.length === 0 && (
                <p className="text-center text-slate-400 py-4">
                  No expenses added yet
                </p>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {members.length === 0 && !isAddingMember && (
        <div className="text-center py-12 text-slate-400">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No household members added yet. Add your first member to get started!</p>
        </div>
      )}
    </div>
  );
};